import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Search, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

export const TelepassImportDialog = ({ open, stations, isAdmin, defaultStation, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [station, setStation] = useState(defaultStation || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (confirm) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("station_id", station || "");
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post("/telepass/import", fd);
      if (confirm) { toast.success(`Importati ${data.count} Telepass (${data.new} nuovi, ${data.update} aggiornati)`); setFile(null); setPreview(null); onDone(); }
      else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="telepass-import-dialog">
        <DialogHeader>
          <DialogTitle>Caricamento massivo Telepass</DialogTitle>
          <DialogDescription>Colonna obbligatoria: NUMERO TELEPASS. Facoltative: NUMERO INTERNO, FURGONE ASSOCIATO (se vuoto resta l'eventuale associazione già presente), DISPONIBILE (SI/NO, vuoto = SI), NON DISPONIBILE PER, AREA C (SI/NO), NOTE{isAdmin && ", STAZIONE"}. I Telepass già presenti (stesso numero) vengono aggiornati.</DialogDescription>
        </DialogHeader>
        {isAdmin && (
          <div className="space-y-1.5">
            <Label>Stazione di riferimento</Label>
            <Select value={station || undefined} onValueChange={(v) => { setStation(v); setPreview(null); }}>
              <SelectTrigger data-testid="telepass-import-station-select"><SelectValue placeholder="Seleziona stazione" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="truncate">{file ? file.name : "Scegli file Excel (.xlsx)..."}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="telepass-import-file-input" />
        </label>
        <Button onClick={() => send(false)} disabled={!file || busy || (isAdmin && !station)} data-testid="telepass-import-preview-button"><Search className="mr-2 h-4 w-4" /> Analizza file</Button>
        {preview && (
          <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="telepass-import-preview">
            <p><b data-testid="telepass-import-count">{preview.count}</b> Telepass validi ({preview.new} nuovi, {preview.update} da aggiornare) · {preview.available} disponibili · {preview.associated} associati a un furgone</p>
            <p className="text-xs text-slate-500">{preview.per_station.map((s) => `${s.station_name}: ${s.count}`).join(" · ")}</p>
            {preview.warnings.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-md bg-amber-50 p-2 text-xs text-amber-800" data-testid="telepass-import-warnings">
                <p className="mb-1 font-semibold">{preview.warnings.length} avvisi (righe importate comunque):</p>
                {preview.warnings.map((w) => <p key={`${w.row}-${w.number}`}>Riga {w.row} ({w.number}): {w.warning}</p>)}
              </div>
            )}
            {preview.errors.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-md bg-rose-50 p-2 text-xs text-rose-700" data-testid="telepass-import-errors">
                <p className="mb-1 font-semibold">{preview.errors.length} righe scartate:</p>
                {preview.errors.map((e) => <p key={`${e.row}-${e.number}`}>Riga {e.row} ({e.number}): {e.error}</p>)}
              </div>
            )}
            <Button onClick={() => send(true)} disabled={busy || !preview.count} data-testid="telepass-import-confirm-button"><Upload className="mr-2 h-4 w-4" /> Importa</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
